import React, { FormEvent, FunctionComponent, useCallback } from 'react';
import styled from 'styled-components';

import { CardList, SaveEmailSubmitButtonGroup } from 'components';
import { FormGroup } from 'components/chroma';
import { PriceCard } from 'scenes/common/components/QuoteInformation/QuotePriceCards/PriceCard';
import { getInstalmentFootnote } from 'scenes/common/helpers/insuranceInfo';
import { replaceSpaces } from 'scenes/common/helpers/text';
import { useIsLargeScreen } from 'scenes/common/hooks/useMedia';
import { AriaLive, FunnelType } from 'scenes/common/types';
import { SCREEN } from 'scenes/common/types/screen';
import {
  getAdditionalExcesses,
  getCampaign,
  getMarketValue,
  getPreselectedPaymentFrequency,
  getSelectedCoverType,
  getValidInsuranceOptions,
  isComprehensiveSelected,
  isMonthlyPaymentPreselected,
  isThirdPartySelected,
} from 'scenes/motor/selectors';
import { MotorFullPageProps } from 'scenes/motor/types';
import config from 'shared/config';

import {
  getComprehensiveExcesses,
  getThirdPartyStandardExcess,
} from '../ExcessStep/ExcessUtils';
import { Footnotes } from './Footnotes/Footnotes';
import SwitchOptionsLink from './SwitchOptionsLink';
import { ClaimsCVPMobile } from '../ClaimsCVPMobile';

const QuotePriceStepWrapper = styled.div`
  label {
    margin-bottom: 0.5em;

    @media (min-width: ${SCREEN.SMALL}px) {
      font-size: 2em !important;
    }
  }
  label + p {
    margin-top: 0.5em !important;
  }
  .c-btn--tertiary:first-of-type {
    padding: 0 1em;
    margin-left: -0.5em !important;
  }
`;

export const QuotePriceStep: FunctionComponent<MotorFullPageProps> = ({
  funnelState,
  onNext,
  onSelectInsuranceOption,
  onInsuranceTypeSwitch,
  onClickSaveQuoteModal,
  onSwitchPaymentFrequency,
}) => {
  const isLargeScreen = useIsLargeScreen();
  const insuranceOptions = getValidInsuranceOptions(funnelState);
  const selectedCoverType = getSelectedCoverType(funnelState);
  const comprehensiveSelected = isComprehensiveSelected(funnelState);
  const thirdPartySelected = isThirdPartySelected(funnelState);
  const thirdPartyCoverLimit = parseInt(config.thirdPartyCoverLimit, 10);
  const additionalExcesses = getAdditionalExcesses(funnelState);
  const { nominatedExcess } = getComprehensiveExcesses(additionalExcesses);
  const thirdpartyStandardExcess = getThirdPartyStandardExcess(
    additionalExcesses
  );
  const excess = comprehensiveSelected
    ? nominatedExcess
    : thirdpartyStandardExcess;

  // IF limit isn't defined, display option
  const displayThirdPartyOptions =
    Number.isNaN(thirdPartyCoverLimit) ||
    parseInt(getMarketValue(funnelState), 10) <= thirdPartyCoverLimit;

  const onSubmit = useCallback(
    (event: FormEvent) => {
      onNext({});
      event.preventDefault();
    },
    [onNext]
  );

  return (
    <form onSubmit={onSubmit}>
      <QuotePriceStepWrapper>
        <FormGroup
          label={
            comprehensiveSelected
              ? 'Our most popular cover'
              : 'Our Third Party cover'
          }
          helpText={
            comprehensiveSelected && displayThirdPartyOptions
              ? 'See more car quotes including Third Party'
              : ''
          }
        >
          {displayThirdPartyOptions ? (
            <SwitchOptionsLink
              thirdPartySelected={thirdPartySelected}
              onInsuranceTypeSwitch={onInsuranceTypeSwitch}
            />
          ) : null}
          <CardList>
            {insuranceOptions.map((option) => {
              const {
                coverType,
                premium: {
                  annualPrice,
                  price,
                  monthlyInstalment,
                  monthlyGovernmentCharges,
                  yearlyGovernmentCharges,
                },
              } = option;
              const savedAmount = Number(price) - Number(annualPrice);
              return (
                <PriceCard
                  key={replaceSpaces(coverType)}
                  frequency={getPreselectedPaymentFrequency(funnelState)}
                  coverType={coverType}
                  selected={selectedCoverType === coverType}
                  amount={
                    isMonthlyPaymentPreselected(funnelState)
                      ? monthlyInstalment
                      : annualPrice
                  }
                  showGovernmentCharges
                  monthlyGovernmentCharges={monthlyGovernmentCharges}
                  yearlyGovernmentCharges={yearlyGovernmentCharges}
                  footnoteText={getInstalmentFootnote(
                    isMonthlyPaymentPreselected(funnelState),
                    price,
                    savedAmount
                  )}
                  onClick={() =>
                    selectedCoverType !== coverType &&
                    onSelectInsuranceOption(option)
                  }
                  onSwitchPaymentFrequency={onSwitchPaymentFrequency}
                  ariaLive={AriaLive.assertive}
                  productType={FunnelType.Motor}
                />
              );
            })}
          </CardList>
        </FormGroup>
        <ClaimsCVPMobile />
        {isLargeScreen && (
          <Footnotes
            comprehensiveSelected={comprehensiveSelected}
            thirdPartySelected={thirdPartySelected}
            campaign={getCampaign(funnelState)}
            excess={excess}
          />
        )}
        <SaveEmailSubmitButtonGroup
          onClickSaveQuoteModal={onClickSaveQuoteModal}
          submitEnabled
        />
        {!isLargeScreen && (
          <Footnotes
            comprehensiveSelected={comprehensiveSelected}
            thirdPartySelected={thirdPartySelected}
            campaign={getCampaign(funnelState)}
            excess={excess}
          />
        )}
      </QuotePriceStepWrapper>
    </form>
  );
};

export default QuotePriceStep;
